import * as React from "react";
import {
    ChevronDown,
    ChevronRight,
    File,
    FileCode,
    FileImage,
    FileJson,
    Folder,
    FolderOpen,
    Plus,
    Trash2,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
    deleteAsset,
    deleteFile,
    listAssets,
    listFiles,
    uploadAsset,
    writeFile,
} from "@/api/constructor";
import type { DraftAssetDto, DraftFileDto } from "@/types/constructor";
import { apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { ViewSelection } from "@/components/constructor/file-view";

interface Props {
    draftId: number;
    selected: ViewSelection | null;
    onSelect: (sel: ViewSelection) => void;
}

interface TreeNode {
    name: string;
    fullPath: string;
    isDir: boolean;
    children: Map<string, TreeNode>;
    leaf?: { kind: "file"; data: DraftFileDto } | { kind: "asset"; data: DraftAssetDto };
}

function buildTree(
    files: DraftFileDto[],
    assets: DraftAssetDto[],
): { fileRoot: TreeNode; assetRoot: TreeNode } {
    const insert = (root: TreeNode, path: string, leaf: TreeNode["leaf"]) => {
        const parts = path.split("/");
        let cur = root;
        for (let i = 0; i < parts.length; i++) {
            const segment = parts[i];
            const isLast = i === parts.length - 1;
            const fullPath = parts.slice(0, i + 1).join("/");
            let next = cur.children.get(segment);
            if (!next) {
                next = {
                    name: segment,
                    fullPath,
                    isDir: !isLast,
                    children: new Map(),
                };
                cur.children.set(segment, next);
            }
            if (isLast) {
                next.isDir = false;
                next.leaf = leaf;
            }
            cur = next;
        }
    };
    const fileRoot: TreeNode = { name: "", fullPath: "", isDir: true, children: new Map() };
    const assetRoot: TreeNode = { name: "", fullPath: "", isDir: true, children: new Map() };
    for (const f of files) insert(fileRoot, f.path, { kind: "file", data: f });
    for (const a of assets) insert(assetRoot, a.path, { kind: "asset", data: a });
    return { fileRoot, assetRoot };
}

function sortChildren(node: TreeNode): TreeNode[] {
    return [...node.children.values()].sort((a, b) => {
        if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
        return a.name.localeCompare(b.name);
    });
}

export function FileTree({ draftId, selected, onSelect }: Props): React.ReactNode {
    const queryClient = useQueryClient();
    const filesQuery = useQuery({
        queryKey: ["draft", draftId, "files"],
        queryFn: () => listFiles(draftId),
    });
    const assetsQuery = useQuery({
        queryKey: ["draft", draftId, "assets"],
        queryFn: () => listAssets(draftId),
    });

    const [newFilePath, setNewFilePath] = React.useState("");
    const [openDirs, setOpenDirs] = React.useState<Set<string>>(() => new Set([""]));

    const newFileMut = useMutation({
        mutationFn: () => writeFile(draftId, newFilePath, ""),
        onSuccess: (file) => {
            queryClient.invalidateQueries({ queryKey: ["draft", draftId, "files"] });
            setNewFilePath("");
            // Open the parent directory of the new file.
            const dir = file.path.includes("/") ? file.path.substring(0, file.path.lastIndexOf("/")) : "";
            setOpenDirs((s) => new Set([...s, dir]));
            onSelect({ kind: "file", path: file.path });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't create file")),
    });

    const deleteFileMut = useMutation({
        mutationFn: (path: string) => deleteFile(draftId, path),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["draft", draftId, "files"] }),
    });

    const uploadAssetMut = useMutation({
        mutationFn: ({ path, file }: { path: string; file: File }) => uploadAsset(draftId, path, file),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["draft", draftId, "assets"] });
            toast.success("Asset uploaded");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Upload failed")),
    });
    const deleteAssetMut = useMutation({
        mutationFn: (path: string) => deleteAsset(draftId, path),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["draft", draftId, "assets"] }),
    });

    const onDropAsset = (e: React.DragEvent) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (!file) return;
        const path = `assets/${file.name}`;
        uploadAssetMut.mutate({ path, file });
    };

    const files: DraftFileDto[] = filesQuery.data || [];
    const assets: DraftAssetDto[] = assetsQuery.data || [];
    const { fileRoot, assetRoot } = React.useMemo(() => buildTree(files, assets), [files, assets]);

    const toggleDir = (fullPath: string) => {
        setOpenDirs((s) => {
            const next = new Set(s);
            if (next.has(fullPath)) next.delete(fullPath);
            else next.add(fullPath);
            return next;
        });
    };

    const renderNode = (
        node: TreeNode,
        depth: number,
        kind: "file" | "asset",
    ): React.ReactNode => {
        if (node.isDir && node.name === "") {
            return sortChildren(node).map((c) => renderNode(c, depth, kind));
        }
        const indent = { paddingLeft: `${depth * 12 + 4}px` };
        if (node.isDir) {
            const isOpen = openDirs.has(node.fullPath);
            return (
                <React.Fragment key={node.fullPath}>
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleDir(node.fullPath)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") toggleDir(node.fullPath);
                        }}
                        className="group flex cursor-pointer items-center gap-1 rounded py-0.5 hover:bg-muted/60"
                        style={indent}
                    >
                        {isOpen
                            ? <ChevronDown className="h-3 w-3 text-muted-foreground" />
                            : <ChevronRight className="h-3 w-3 text-muted-foreground" />}
                        {isOpen
                            ? <FolderOpen className="h-3.5 w-3.5 text-muted-foreground" />
                            : <Folder className="h-3.5 w-3.5 text-muted-foreground" />}
                        <span className="text-xs">{node.name}/</span>
                    </div>
                    {isOpen && sortChildren(node).map((c) => renderNode(c, depth + 1, kind))}
                </React.Fragment>
            );
        }
        if (kind === "file" && node.leaf?.kind === "file") {
            const f = node.leaf.data;
            const isSelected = selected?.kind === "file" && selected.path === f.path;
            return (
                <div
                    key={f.path}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelect({ kind: "file", path: f.path })}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") onSelect({ kind: "file", path: f.path });
                    }}
                    data-testid={`file-row-${f.path}`}
                    className={cn(
                        "group flex cursor-pointer items-center gap-1 rounded py-0.5 hover:bg-muted/60",
                        isSelected && "bg-muted font-medium",
                    )}
                    style={indent}
                >
                    <span className="w-3" />
                    <FileIconFor path={f.path} />
                    <span className="flex-1 truncate text-xs">{node.name}</span>
                    <span className="hidden text-[10px] text-muted-foreground group-hover:inline">
                        {f.sizeBytes}b
                    </span>
                    <button
                        type="button"
                        aria-label={`Delete ${f.path}`}
                        className="opacity-0 transition-opacity group-hover:opacity-100"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(`Delete ${f.path}?`)) deleteFileMut.mutate(f.path);
                        }}
                    >
                        <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                    </button>
                </div>
            );
        }
        if (kind === "asset" && node.leaf?.kind === "asset") {
            const a = node.leaf.data;
            const isSelected = selected?.kind === "asset" && selected.path === a.path;
            return (
                <div
                    key={a.path}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelect({ kind: "asset", path: a.path })}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") onSelect({ kind: "asset", path: a.path });
                    }}
                    data-testid={`asset-row-${a.path}`}
                    className={cn(
                        "group flex cursor-pointer items-center gap-1 rounded py-0.5 hover:bg-muted/60",
                        isSelected && "bg-muted font-medium",
                    )}
                    style={indent}
                >
                    <span className="w-3" />
                    <FileImage className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="flex-1 truncate text-xs">{node.name}</span>
                    <span className="hidden text-[10px] text-muted-foreground group-hover:inline">
                        {Math.round(a.sizeBytes / 1024)}k
                    </span>
                    <button
                        type="button"
                        aria-label={`Delete ${a.path}`}
                        className="opacity-0 transition-opacity group-hover:opacity-100"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(`Delete asset ${a.path}?`)) deleteAssetMut.mutate(a.path);
                        }}
                    >
                        <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                    </button>
                </div>
            );
        }
        return null;
    };

    return (
        <div
            className="flex h-full flex-col gap-3 border-r border-border bg-background p-3 text-sm"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDropAsset}
        >
            <div className="flex items-center gap-1.5">
                <Input
                    value={newFilePath}
                    placeholder="folder/new-file.html"
                    onChange={(e) => setNewFilePath(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && newFilePath.trim()) newFileMut.mutate();
                    }}
                    className="h-7 text-xs"
                    data-testid="new-file-path"
                />
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!newFilePath.trim() || newFileMut.isPending}
                    onClick={() => newFileMut.mutate()}
                    data-testid="new-file-add"
                >
                    <Plus className="h-3 w-3" />
                </Button>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Files {files.length > 0 && <span className="text-muted-foreground/70">({files.length})</span>}
                </div>
                {filesQuery.isLoading && <Spinner className="h-4 w-4" />}
                {!filesQuery.isLoading && files.length === 0 && (
                    <div className="text-xs text-muted-foreground">No files yet — create one above or use chat.</div>
                )}
                <div data-testid="file-tree">{renderNode(fileRoot, 0, "file")}</div>

                <div className="mb-1 mt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Assets {assets.length > 0 && <span className="text-muted-foreground/70">({assets.length})</span>}
                </div>
                {assets.length === 0 ? (
                    <div className="text-xs text-muted-foreground">Drop an image/file here to add an asset.</div>
                ) : (
                    <div data-testid="asset-tree">{renderNode(assetRoot, 0, "asset")}</div>
                )}
            </div>
        </div>
    );
}

function FileIconFor({ path }: { path: string }): React.ReactNode {
    if (path.endsWith(".json")) return <FileJson className="h-3.5 w-3.5 text-muted-foreground" />;
    if (path.endsWith(".html") || path.endsWith(".css") || path.endsWith(".js") || path.endsWith(".ts"))
        return <FileCode className="h-3.5 w-3.5 text-muted-foreground" />;
    return <File className="h-3.5 w-3.5 text-muted-foreground" />;
}
